import { useState, useEffect, useContext, useCallback } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

/**
 * Hook para cargar y gestionar los alias personalizados de las columnas adicionales
 * Carga los settings con keys "Columna 4" a "Columna 10" y devuelve un objeto con los alias
 * 
 * @returns {Object} Objeto con los alias: { "Columna 4": "DNI", "Columna 5": "Fecha", ... }
 */
const useColumnAliases = () => {
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const companyId = user?.companyId;
  const [aliases, setAliases] = useState({
    "Columna 4": "Columna 4",
    "Columna 5": "Columna 5",
    "Columna 6": "Columna 6",
    "Columna 7": "Columna 7",
    "Columna 8": "Columna 8",
    "Columna 9": "Columna 9",
    "Columna 10": "Columna 10"
  });

  const loadAliases = useCallback(async (isMounted) => {
    if (!companyId) return;

    try {
      const { data } = await api.get("/settings");
      
      // ✅ CORRECCIÓN: Verificar si el componente sigue montado antes de actualizar estado
      if (!isMounted || !isMounted.current) return;
      
      // Filtrar solo los settings de columnas (Columna 4 a Columna 10)
      const columnKeys = ["Columna 4", "Columna 5", "Columna 6", "Columna 7", "Columna 8", "Columna 9", "Columna 10"];
      const columnSettings = data.filter(setting => columnKeys.includes(setting.key));
      
      // Crear objeto con los alias desde la base de datos
      // Los datos deberían estar cargados por SEEDS, pero si no existen, usar key como fallback
      const newAliases = {};
      columnKeys.forEach(key => {
        const setting = columnSettings.find(s => s.key === key);
        // Si existe en la base de datos, usar su value; si no, usar key (fallback de seguridad)
        newAliases[key] = setting?.value || key;
      });
      
      setAliases(newAliases);
    } catch (err) {
      // En caso de error, mantener valores por defecto (fallback de seguridad)
      console.warn("Error al cargar alias de columnas desde Settings, usando valores por defecto:", err);
      // No mostrar toastError aquí porque los valores por defecto son suficientes
    }
  }, [companyId]);

  // Cargar alias al montar el componente
  useEffect(() => {
    // ✅ CORRECCIÓN: Usar ref para rastrear si el componente está montado
    const isMounted = { current: true };
    
    loadAliases(isMounted);
    
    // Cleanup: marcar como desmontado
    return () => {
      isMounted.current = false;
    };
  }, [loadAliases]);

  // Escuchar cambios en settings vía socket
  useEffect(() => {
    if (!companyId) return;

    const socket = socketManager.getSocket(companyId);

    if (!socket) return;

    const handleSettingsUpdate = (data) => {
      if (data.action === "update" && data.setting) {
        const key = data.setting.key;
        // Si se actualiza un setting de columna, recargar todos los alias
        if (key.startsWith("Columna ")) {
          // ✅ CORRECCIÓN: Usar ref para verificar si está montado
          const isMounted = { current: true };
          loadAliases(isMounted);
        }
      }
    };

    socket.on(`company-${companyId}-settings`, handleSettingsUpdate);

    return () => {
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-settings`, handleSettingsUpdate);
      }
    };
  }, [companyId, socketManager, loadAliases]);

  // Función helper para obtener el alias de una columna
  const getAlias = useCallback((columnKey) => {
    return aliases[columnKey] || columnKey;
  }, [aliases]);

  return {
    aliases,
    getAlias
  };
};

export default useColumnAliases;

